import React, { useState, useRef } from "react";
import * as S from "./styles.ts";
import Loading from "../Loading/index.tsx";
import { TextField } from "@mui/material";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormGetValues,
  Control,
  Controller,
  FieldPath,
  Path,
  PathValue,
} from "react-hook-form";
import enderecoService from "../../services/enderecoService.ts";
import { maskCep } from "../../utils/address.util.ts";

type EnderecoProps<T extends Record<string, any>> = {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  control: Control<T>;
  basePath?: FieldPath<T>;
  readOnly?: boolean;
};

const Endereco = <T extends Record<string, any>>({
  register,
  errors,
  setValue,
  getValues,
  control,
  basePath = "endereco" as FieldPath<T>,
  readOnly = false,
}: EnderecoProps<T>) => {
  const [loading, setLoading] = useState(false);
  const [cep, setCep] = useState("");
  const { showFeedback } = useFeedback();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCepChange = (value: string) => {
    const raw = value.replace(/\D/g, "");
    if (raw.length > 8) return;

    setCep(raw);
    setValue(`${basePath}.cep` as FieldPath<T>, raw as PathValue<T, Path<T>>);

    if (raw.length === 8) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        setLoading(true);
        await enderecoService.getAddressByZipCode(
          raw,
          basePath,
          setValue,
          showFeedback
        );
        setLoading(false);
        setCep((prev) => maskCep(prev));
        setTimeout(() => setCep((prev) => prev.trim()), 50);
      }, 200);
    }
  };

  return (
    <S.ModalContainer>
      {loading && <Loading />}
      <S.InputGroup>
        <TextField
          label="CEP"
          variant="outlined"
          value={
            getValues(`${basePath}.cep` as Path<T>)
              ? maskCep(getValues(`${basePath}.cep` as Path<T>).toString())
              : maskCep(cep)
          }
          onChange={(e) => handleCepChange(e.target.value)}
          error={!!(errors as any)?.[basePath]?.cep}
          helperText={(errors as any)?.[basePath]?.cep?.message}
          sx={{ width: "100%" }}
          size="small"
          disabled={loading || readOnly}
        />

        {["bairro", "cidade", "uf", "rua", "numero"].map((field) => (
          <Controller
            key={field}
            name={`${basePath}.${field}` as Path<T>}
            control={control}
            render={({ field: controllerField }) => (
              <TextField
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                variant="outlined"
                error={!!(errors as any)?.[basePath]?.[field]}
                disabled={readOnly}
                helperText={(errors as any)?.[basePath]?.[field]?.message}
                {...controllerField}
                value={controllerField.value ?? ""}
                sx={{ width: "100%" }}
                size="small"
              />
            )}
          />
        ))}
      </S.InputGroup>
    </S.ModalContainer>
  );
};

export default Endereco;
